import type Prompt from "@/models/prompt/prompt";
import usePrompts from "@/hooks/usePrompts";
import useTags from "@/hooks/useTags";
import type Tag from "@/models/tag/tag";
import type { TagType } from "@/models/tag/tag";
import { useEffect, useState } from "react";
import type { PromptSchemaType } from "@/pages/chat/components/prompt-library/PromptLibraryForm";
import {
  showSuccessToast,
  showErrorToast,
} from "@/components/custom-toasts/toasts";

interface UsePromptsCrudReturn {
  tags: Tag[];
  handleCreateTag: (name: string, tagType: TagType) => Promise<boolean>;
  handleUpdatePrompt: (
    promptId: string,
    values: PromptSchemaType
  ) => Promise<boolean>;
  isUpdating: boolean;
}

const usePromptsCrud = (): UsePromptsCrudReturn => {
  // Hooks
  const { update } = usePrompts();
  const { useQueryPagination: tagPagination, create } = useTags();

  // Data state
  const [tags, setTags] = useState<Tag[]>([]);

  // Fetch tags
  const fetchedTags = tagPagination({
    pageNumber: 1,
    pageSize: 1000000,
    sortAscending: true,
  });

  useEffect(() => {
    if (fetchedTags.isFetched && fetchedTags.data && fetchedTags.data.data) {
      setTags(fetchedTags.data.data);
    }
  }, [fetchedTags.data, fetchedTags.isFetched]);

  const handleCreateTag = async (name: string, tagType: TagType) => {
    try {
      await create.mutateAsync({ name, tagType });

      showSuccessToast("Tagget er blevet oprettet");

      return true;
    } catch (error) {
      showErrorToast(
        `Der skete en fejl ved oprettelse af tagget: ${error instanceof Error ? error.message : String(error)}`
      );
      return false;
    }
  };

  const handleUpdatePrompt = async (
    promptId: string,
    values: PromptSchemaType
  ) => {
    try {
      const promptToUpdate: Partial<Prompt> = {
        promptString: values.promptString,
        tags: values.tags as Tag[],
        description: values.description,
        title: values.title,
        status: values.status,
      };

      await update.mutateAsync({ id: promptId, ...promptToUpdate });

      showSuccessToast("Prompten er blevet opdateret");

      return true;
    } catch (error) {
      showErrorToast(
        `Der skete en fejl ved opdatering af prompten: ${error instanceof Error ? error.message : String(error)}`
      );
      return false;
    }
  };

  return {
    tags,
    handleUpdatePrompt,
    handleCreateTag,
    isUpdating: update.isPending,
  };
};

export default usePromptsCrud;
